export enum MediaLinkType {
  VIDEO_LINK = 'videoLink',
  VIRTUAL_TOUR_LINK = 'virtualTourLink',
  EXTERNAL_LINK = 'externalLink',
}

export interface ValidationErrors {
  [MediaLinkType.VIDEO_LINK]?: string;
  [MediaLinkType.VIRTUAL_TOUR_LINK]?: string;
  [MediaLinkType.EXTERNAL_LINK]?: string;
}

export class MediaLinksValidator {
  private static readonly YOUTUBE_PATTERNS = [
    /^https?:\/\/(www\.)?youtube\.com\/watch\?v=[\w-]+/,
    /^https?:\/\/youtu\.be\/[\w-]+(\?.*)?$/i,
  ];

  private static readonly MATTERPORT_PATTERN =
    /^https?:\/\/(my\.)?matterport\.com\/show\/\?m=[\w-]+(&.*)?$/i;

  private static readonly ERROR_MESSAGES = {
    [MediaLinkType.VIDEO_LINK]: 'Please enter a valid YouTube link',
    [MediaLinkType.VIRTUAL_TOUR_LINK]: 'Please enter a valid Matterport link',
    [MediaLinkType.EXTERNAL_LINK]: 'Please enter a valid URL',
  } as const;

  static validateYouTubeLink(url: string): string | undefined {
    if (!url.trim()) return undefined;

    const isValid = this.YOUTUBE_PATTERNS.some((pattern) => pattern.test(url));
    return isValid ? undefined : this.ERROR_MESSAGES[MediaLinkType.VIDEO_LINK];
  }

  static validateMatterportLink(url: string): string | undefined {
    if (!url.trim()) return undefined;

    const isValid = this.MATTERPORT_PATTERN.test(url);
    return isValid ? undefined : this.ERROR_MESSAGES[MediaLinkType.VIRTUAL_TOUR_LINK];
  }

  static validateURL(url: string): string | undefined {
    if (!url.trim()) return undefined;

    try {
      new URL(url);
      return undefined;
    } catch {
      return this.ERROR_MESSAGES[MediaLinkType.EXTERNAL_LINK];
    }
  }

  static validateMediaLinkType(type: MediaLinkType, value: string): string | undefined {
    switch (type) {
      case MediaLinkType.VIDEO_LINK:
        return this.validateYouTubeLink(value);
      case MediaLinkType.VIRTUAL_TOUR_LINK:
        return this.validateMatterportLink(value);
      case MediaLinkType.EXTERNAL_LINK:
        return this.validateURL(value);
      default:
        return undefined;
    }
  }

  static validateAllLinks(mediaLinks: {
    videoLink?: string;
    virtualTourLink?: string;
    externalLink?: string;
  }): ValidationErrors {
    return {
      [MediaLinkType.VIDEO_LINK]: this.validateMediaLinkType(
        MediaLinkType.VIDEO_LINK,
        mediaLinks.videoLink || ''
      ),
      [MediaLinkType.VIRTUAL_TOUR_LINK]: this.validateMediaLinkType(
        MediaLinkType.VIRTUAL_TOUR_LINK,
        mediaLinks.virtualTourLink || ''
      ),
      [MediaLinkType.EXTERNAL_LINK]: this.validateMediaLinkType(
        MediaLinkType.EXTERNAL_LINK,
        mediaLinks.externalLink || ''
      ),
    };
  }

  static hasValidationErrors(errors: ValidationErrors): boolean {
    return Object.values(errors).some((error) => error !== undefined);
  }
}

export const MAX_RETRY_ATTEMPTS = 3;
export const INITIAL_RETRY_DELAY_MS = 1000;

export async function retryWithDelay<T>(
  fn: () => Promise<T>,
  maxAttempts: number = MAX_RETRY_ATTEMPTS,
  initialDelay: number = INITIAL_RETRY_DELAY_MS
): Promise<T> {
  let lastError: Error | unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (error instanceof Error && 'status' in error) {
        const status = (error as any).status;
        if (status >= 400 && status < 500 && status !== 429) {
          throw error;
        }
      }

      if (attempt < maxAttempts) {
        const delayMs = initialDelay * Math.pow(2, attempt - 1);
        await new Promise((res) => setTimeout(res, delayMs));
      }
    }
  }

  throw lastError;
}
